import React from 'react';
import { MapPin, Plane } from 'lucide-react';

/** In/out station picker — shows live configured out-station approval path. */
export default function StationTypeFields({ stationType, setStationType, workflow }) {
  const steps = workflow?.steps || [];
  const pathLabel = workflow?.path_label || '';
  const leadership = steps.filter((s) => s.stage !== 'doctor');

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-heading text-xl text-stone-900">In-Station / Out-Station</h2>
        <p className="text-sm text-stone-500">
          {pathLabel
            ? `Out-station referrals follow: ${pathLabel}.`
            : 'Out-station referrals need leadership approval before the campus doctor prepares the letter.'}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Choice
          active={stationType === 'in_station'}
          onClick={() => setStationType('in_station')}
          icon={MapPin}
          title="In-Station"
          body="Referral within Hyderabad / local centres. Goes directly to the campus doctor queue."
        />
        <Choice
          active={stationType === 'out_station'}
          onClick={() => setStationType('out_station')}
          icon={Plane}
          title="Out-Station"
          body="Referral outside station. Uses the approval stages configured by hospital leadership."
          warn
        />
      </div>
      {stationType === 'out_station' && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 space-y-2">
          <p>
            <strong>Leadership approval required.</strong>
            {leadership.length
              ? ' Your request will move through these configured stages, then to your preferred doctor for the letter:'
              : ' After final approval, your preferred doctor prepares the letter.'}
          </p>
          {leadership.length > 0 ? (
            <ol className="list-decimal pl-5 space-y-1 text-amber-900">
              {leadership.map((s) => (
                <li key={s.stage}>
                  <span className="font-medium">{s.label}</span>
                  {s.assignee ? ` — ${s.assignee}` : ''}
                </li>
              ))}
              <li>
                <span className="font-medium">Preferred doctor</span>
                {' — prepares the referral letter'}
              </li>
            </ol>
          ) : pathLabel ? (
            <p className="text-amber-900">{pathLabel}</p>
          ) : null}
        </div>
      )}
    </section>
  );
}

function Choice({ active, onClick, icon: Icon, title, body, warn }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border px-4 py-4 text-left transition ${
        active
          ? warn
            ? 'border-amber-500 bg-amber-50 shadow-sm'
            : 'border-[var(--brand)] bg-orange-50 shadow-sm'
          : 'border-stone-200 bg-white hover:border-stone-300'
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className={`rounded-full p-2 ${active ? 'bg-white text-[var(--brand)]' : 'bg-stone-100 text-stone-600'}`}>
          <Icon className="h-4 w-4" />
        </span>
        <p className="font-semibold text-stone-900">{title}</p>
      </div>
      <p className="text-xs leading-relaxed text-stone-500">{body}</p>
    </button>
  );
}
